import { useEffect, useState } from 'react';
import { Link, createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Field, Input, PasswordInput } from '@/components/ui/field';
import { Dialog, DialogClose, DialogContent, DialogTrigger } from '@/components/ui/dialog';
import { ErrorNote, Spinner } from '@/components/ui/feedback';
import { useToast } from '@/components/ui/toast';
import { authStore } from '@/lib/auth-store';
import { meQuery, isStaff } from '@/lib/queries';
import { errorMessage } from '@/lib/api';
import { LOGO_URL } from '@/lib/asset';
import { AuthLayout } from '@/components/auth-layout';
import { signInElsewhere } from '@/lib/session-exit';
import { TabFileError } from '@tabservice/tabfile-client';

const searchSchema = z.object({
  redirect: z.string().optional(),
});

export const Route = createFileRoute('/sign-in')({
  validateSearch: searchSchema,
  beforeLoad: async ({ context, preload }) => {
    const state = await context.auth.ready();
    if (state.status === 'signed-in') throw redirect({ to: '/' });
    // Sign-in belongs to the portal. Leave before the form is drawn, and never resolve, so
    // nothing renders in the moment the browser takes to go.
    const away = signInElsewhere();
    // Links preload on hover (defaultPreload: 'intent'). Pointing at a Sign in link is not
    // asking to sign in, so a preload never leaves the page; only the real visit does.
    if (away && preload) return;
    if (away) {
      window.location.replace(away);
      await new Promise(() => {});
    }
  },
  component: SignInPage,
});

const formSchema = z.object({
  email: z.string().min(1, 'Enter your email address').email('That does not look like an email address'),
  password: z.string().min(1, 'Enter your password'),
});

type FormValues = z.infer<typeof formSchema>;

function SignInPage() {
  const navigate = useNavigate();
  const search = Route.useSearch();
  const { queryClient } = Route.useRouteContext();
  // The reason the API refused the last session, handed over in memory by the route gate.
  const [formError, setFormError] = useState<string | null>(() => authStore.consumeDeniedReason());

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { email: '', password: '' },
  });

  // Editing either field clears the last failure; the note also has its own close control.
  useEffect(() => {
    const subscription = form.watch(() => setFormError(null));
    return () => subscription.unsubscribe();
  }, [form]);

  const onSubmit = form.handleSubmit(async (values) => {
    setFormError(null);
    try {
      await authStore.signIn(values.email, values.password);
      // Whatever the previous session had fetched is not this person's.
      queryClient.clear();
      // A network failure here is almost always the API restarting or a flaky link, so keep
      // trying for about ten seconds before showing anything. A refusal (4xx) is not retried.
      const me = await queryClient.fetchQuery({
        ...meQuery(),
        retry: (failureCount, error) => !(error instanceof TabFileError) && failureCount < 4,
        retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 4000),
      });
      if (isStaff(me)) {
        await navigate({ to: '/admin' });
        return;
      }
      await navigate({ to: search.redirect ?? '/' });
    } catch (error) {
      // Roll the whole thing back.
      //
      // Firebase has its own session, and it may well have accepted the credentials before
      // this failed - the API being unreachable is the usual reason to land here. Leaving
      // that session in place means the person is told sign-in failed while the browser
      // quietly holds a signed-in session: reload, and the application shell opens, fails to
      // load anything, and offers Sign out as the only way back. Signing in either finishes
      // or leaves no trace of itself.
      await authStore.signOut().catch(() => {});
      queryClient.clear();
      setFormError(errorMessage(error));
    }
  });

  return (
    <AuthLayout>
      <img
        src={LOGO_URL}
        alt="TAB Service Company"
        width={130}
        height={40}
        className="h-10 w-auto lg:hidden"
      />
      <div>
        <h1 className="text-[28px] leading-9 font-semibold text-primary">Sign in</h1>
        <p className="mt-2 text-base text-muted-foreground">Use the account Tab Service set up for your company.</p>
      </div>

      <form className="flex flex-col gap-4" onSubmit={onSubmit} noValidate>
        <Field label="Email" error={form.formState.errors.email?.message}>
          {(props) => (
            <Input
              {...props}
              {...form.register('email')}
              type="email"
              autoComplete="username"
              autoFocus
              placeholder="you@company.com"
            />
          )}
        </Field>

        <Field label="Password" error={form.formState.errors.password?.message}>
          {(props) => <PasswordInput {...props} {...form.register('password')} autoComplete="current-password" />}
        </Field>

        <div className="flex justify-end">
          <ForgotPassword defaultEmail={form.watch('email')} />
        </div>

        {formError ? (
          <ErrorNote title="Sign in failed" detail={formError} onDismiss={() => setFormError(null)} />
        ) : null}

        <Button type="submit" variant="primary" size="lg" className="w-full" disabled={form.formState.isSubmitting}>
          {form.formState.isSubmitting ? <Spinner /> : null}
          Sign in
        </Button>
      </form>

      <p className="mt-6 border-t border-[color:var(--ts-border-soft)] pt-5 text-base text-muted-foreground">
        Still on FIRE? The{' '}
        <Link to="/tools/fire-to-iris" className="font-medium text-link hover:underline">
          FIRE to IRIS converter
        </Link>{' '}
        reads a Publication 1220 file and writes the IRIS XML, here in your browser. Signing in is not part
        of it.
      </p>
    </AuthLayout>
  );
}

function ForgotPassword({ defaultEmail }: { defaultEmail: string }) {
  const toast = useToast();
  const [email, setEmail] = useState(defaultEmail);
  const [sending, setSending] = useState(false);
  const [open, setOpen] = useState(false);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) setEmail(defaultEmail);
      }}
    >
      <DialogTrigger asChild>
        <button
          type="button"
          className="rounded-md text-base font-medium text-link transition-colors duration-150 ease-out hover:text-primary hover:underline"
        >
          Forgot your password?
        </button>
      </DialogTrigger>
      <DialogContent
        title="Reset your password"
        description="A link to set a new password will be sent to this address."
        footer={
          <>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button
              variant="primary"
              disabled={sending || email.trim() === ''}
              onClick={async () => {
                setSending(true);
                try {
                  await authStore.sendPasswordReset(email);
                  toast.success('Check your inbox', `If ${email} has an account, a reset link is on its way.`);
                  setOpen(false);
                } catch (error) {
                  toast.error('Could not send the link', errorMessage(error));
                } finally {
                  setSending(false);
                }
              }}
            >
              {sending ? <Spinner /> : null}
              Send the link
            </Button>
          </>
        }
      >
        <Field label="Email">
          {(props) => (
            <Input
              {...props}
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="you@company.com"
            />
          )}
        </Field>
      </DialogContent>
    </Dialog>
  );
}
